import React from 'react';
import GaugeChart from 'react-gauge-chart';
import campaignBg from '../../assets/dashback.png';

import { FaRegClock } from 'react-icons/fa';
import { RiFileWarningLine } from 'react-icons/ri';

const FaRegClockComponent = FaRegClock as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const RiFileWarningLineComponent = RiFileWarningLine as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface TopCardsProps {
  dummyData?: any;
  loading?: boolean;
}

const TopCards: React.FC<TopCardsProps> = ({ dummyData, loading }) => {
  const data = dummyData;

  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 p-8 text-center">Loading overview...</div>;
  }

  if (!data) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 p-8 text-center">No data available</div>;
  }

  const lastSent = data?.lastSentCampaign || {};

  // Check if there is meaningful campaign data
  const hasValidCampaign = !!(
    lastSent.campaignId &&
    lastSent.title &&
    lastSent.title !== "No Campaign"
  );

  // Map campaign data
  const lastSentCampaign = {
    title: lastSent.title || "No Campaign Yet",
    date: lastSent.sentAt
      ? new Date(lastSent.sentAt).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
      : '—',
    image: lastSent.thumbnailUrl || campaignBg,
    recipients: lastSent.recipients || 0,
    opened: `${lastSent.openRate || 0}%`,
    clicked: `${lastSent.clickRate || 0}%`,
    ctor: `${lastSent.ctor || 0}%`,
  };

  const engagementGauge = data?.engagementGauge || null;

  const engagementScore = engagementGauge ? {
    score: engagementGauge.label || "NEW",
    comparison: `Your open rate is ${engagementGauge.deltaVsIndustry || 0}% vs industry average`,
    percentage: (engagementGauge.score || 0) / 100,
    isPoor: engagementGauge.label === "POOR",
  } : null;

  // ==================== EMPTY STATE ====================
  if (!hasValidCampaign) {
    return (
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-6'>
        {/* Left Card - Empty State */}
        <div
          className='md:col-span-2 p-6 rounded-lg flex flex-col items-center justify-center text-center'
          style={{
            backgroundImage: `url(${campaignBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '220px'
          }}
        >
          <div className='text-white flex flex-col items-center'>
            <RiFileWarningLineComponent className='h-12 w-12 text-white mb-4' />
            <h2 className='text-xl font-semibold'>No campaigns yet</h2>
            <p className='mt-2 text-sm opacity-90'>Start by creating your first campaign.</p>
            <button className='mt-6 bg-[#6366F1] hover:bg-[#4F46E5] text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors'>
              + Create Campaign
            </button>
          </div>
        </div>

        {/* Right Card - Engagement Score Empty */}
        <div className='bg-gray-800 text-white p-6 rounded-lg flex flex-col items-center justify-center'>
          <div className='w-full flex flex-col items-center'>
            <div className='w-28 h-14 border-4 border-gray-600 rounded-full flex items-center justify-center mb-4'>
              <div className='w-2 h-2 bg-white rounded-full animate-pulse'></div>
            </div>
            <p className='text-gray-400 text-center text-sm'>
              Your engagement score will appear after sending your first campaign.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ==================== NORMAL STATE ====================
  return (
    <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-6'>
      {/* Left Card - Campaign Info */}
      <div
        className='md:col-span-2 bg-teal-100 p-4 rounded-lg relative'
        style={{
          backgroundImage: `url(${campaignBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className='flex items-center mt-2'>
          <img
            src={lastSentCampaign.image}
            alt='Campaign'
            className='w-16 h-16 mr-4 rounded object-cover'
            style={{ width: '139px', height: '93px' }}
          />
          <div>
            <p className='text-sm' style={{ color: '#1F2937', fontSize: '16px' }}>
              Last Sent Campaign
            </p>
            <p className='font-medium mt-2' style={{ color: '#FFFFFF', fontSize: '20px' }}>
              {lastSentCampaign.title}
            </p>
            <p className='mt-1 flex items-center gap-2' style={{ color: '#FFFFFF', fontSize: '12px' }}>
              <FaRegClockComponent /> {lastSentCampaign.date}
            </p>
          </div>
        </div>

        <div className='flex justify-between mt-9 text-sm pt-4'>
          <div className='pl-4 text-white' style={{ borderLeft: '4px solid #66CDDB' }}>
            Recipients <span className='font-bold block text-xl'>{lastSentCampaign.recipients}</span>
          </div>
          <div className='pl-4 text-white' style={{ borderLeft: '4px solid #66CDDB' }}>
            Opened <span className='font-bold block text-xl'>{lastSentCampaign.opened}</span>
          </div>
          <div className='pl-4 text-white' style={{ borderLeft: '4px solid #66CDDB' }}>
            Clicked <span className='font-bold block text-xl'>{lastSentCampaign.clicked}</span>
          </div>
          <div className='pl-4 text-white' style={{ borderLeft: '4px solid #66CDDB' }}>
            CTOR <span className='font-bold block text-xl'>{lastSentCampaign.ctor}</span>
          </div>
        </div>
      </div>

      {/* Right Card - Engagement Score */}
      {engagementScore ? (
        <div className='bg-gray-800 text-white p-4 rounded-lg flex flex-col items-center'>
          <div className='relative w-full max-w-[250px] mx-auto flex justify-center pt-4'>
            <GaugeChart
              id='gauge-chart'
              nrOfLevels={20}
              colors={['#EA4228', '#F5CD19', '#5BE12C']}
              arcWidth={0.3}
              percent={engagementScore.percentage}
              textColor='transparent'
              style={{ width: '100%', height: '160px' }}
            />
            <div className='absolute inset-0 flex flex-col items-center justify-end translate-y-4 mb-5'>
              <p className='text-xl font-bold flex items-center' style={{
                color: engagementScore.isPoor ? '#EA4228' : '#6EE7B7',
                fontSize: '28px',
              }}>
                {engagementScore.score} {!engagementScore.isPoor && '👍'}
              </p>
              <p className='text-sm text-gray-300' style={{ fontSize: '12px', marginTop: '5px' }}>
                Engagement Score
              </p>
            </div>
          </div>
          <p className='text-xs text-gray-400 mt-6 text-center px-4'>
            {engagementScore.comparison}
          </p>
        </div>
      ) : (
        <div className='bg-gray-800 text-white p-6 rounded-lg flex flex-col items-center justify-center'>
          <p className='text-gray-400 text-center text-sm'>
            Engagement score will appear soon.
          </p>
        </div>
      )}
    </div>
  );
};

export default TopCards;